import { Text } from "@/components/ui/Text";
import { Title } from "@/components/ui/Title";
import { useStore } from "@/store";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
  useActiveAssignment,
  useAssignmentPlaylists,
  useCurrentUserAssignmentResponses,
  useIsAssignmentAdmin,
  useIsAssignmentOwner,
  useIsAssignmentStudent,
  useUserAssignments,
  useUserDidConsentToAssignment,
} from "@/store/selectors/assignmentSelectors";
import { useStyles } from "@/styles/useStyles";
import {
  Button,
  Divider,
  Group,
  lighten,
  Loader,
  Space,
  Stack,
} from "@mantine/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { PlaylistCard } from "@/components/playlists/PlaylistCard";
import { useUserPlaylistData } from "@/store/selectors/playlistSelectors";
import { AssignmentService } from "@/services/AssignmentService";
import { useIsStudentApp } from "@/store/selectors/featureSelectors";
import { AssignmentResults } from "@/components/assignments/AssignmentResults";
import {
  useGetAssignmentColor,
  UserAssignmentStatus,
} from "@/components/assignments/UserAssignmentStatus";
import {
  useUserVideosWatched,
  useUserVideoData,
} from "@/store/selectors/userProfileSelectors";
import { AssignmentChecklistItems } from "@/components/assignments/AssignmentChecklistItems";
import { AssignmentConsentConfirmation } from "@/components/assignments/AssignmentConsentConfirmation";
import {
  IconBrain,
  IconDownload,
  IconFilter,
  IconSearch,
} from "@tabler/icons-react";
import { Analytics } from "@/services/Analytics";
import {
  useActivePartnerAdvizerCount,
  useActiveUser,
  useActiveUserPartner,
} from "@/store/selectors";
import { SupportVideoButton } from "@/components/support/SupportVideoButton";
import { AssignmentSessionScheduler } from "@/components/assignments/AssignmentSessionScheduler";

const MAX_WIDTH = 650;
export function AssignmentPage() {
  const exportRef = useRef<HTMLDivElement>(null);

  const { assignmentId } = useParams();
  const [searchParams] = useSearchParams();
  const { theme, ...styles } = useStyles();
  const activeUser = useActiveUser();
  const isAssignmentAdmin = useIsAssignmentAdmin();
  const activeAssignment = useActiveAssignment();
  const assignmentPlaylists = useAssignmentPlaylists();
  const userPlaylistData = useUserPlaylistData();
  const userAssignments = useUserAssignments();
  const setFullActiveAssignment = useStore(
    (state) => state.partnerApp.setFullActiveAssignment,
  );
  const activeUserPartner = useActiveUserPartner();
  const activePartnerAdvizerCount = useActivePartnerAdvizerCount();
  const isStudentApp = useIsStudentApp();
  const isAssignmentOwner = useIsAssignmentOwner();
  const isStudentView = !!searchParams.get("student");
  // const isExportView = !!searchParams.get("export");
  const [isExporting, setIsExporting] = useState(false);
  const isExportView = isExporting;
  const userVideoData = useUserVideoData();
  const userVideosWatched = useUserVideosWatched();
  const currentUserAssignmentResponses = useCurrentUserAssignmentResponses({
    assignmentId: activeAssignment?.id,
  });
  const setActiveFilterPartnerId = useStore(
    (state) => state.advizerSearch.setActiveFilterPartnerId,
  );
  const isSpecificPlaylistsAssignment =
    activeAssignment?.assignmentType === "specificPlaylists";

  const isAnyAdvizersAssignment =
    activeAssignment?.assignmentType === "anyAdvizers";

  const isCareerLeaderUploadAssignment =
    activeAssignment?.uploadType === "careerLeader";

  const assignmentColor = useGetAssignmentColor({
    userPlaylistData,
    userVideoData,
    userVideosWatched,
    userResponses: currentUserAssignmentResponses,
    assignmentPrompts: activeAssignment?.prompts,
  });
  const userDidConsentToAssignment = useUserDidConsentToAssignment({
    assignmentId: activeAssignment?.id,
  });
  const isAssignmentStudent = useIsAssignmentStudent();

  useEffect(() => {
    if (assignmentId && userAssignments) {
      const assignment = userAssignments[assignmentId];
      if (isAssignmentAdmin) {
        if (assignment) {
          setFullActiveAssignment(assignment);
        }
      } else {
        setFullActiveAssignment(assignment);
      }
    }
  }, [
    assignmentId,
    userAssignments,
    setFullActiveAssignment,
    activeAssignment,
    isAssignmentAdmin,
  ]);

  useEffect(() => {
    if (isAssignmentAdmin && assignmentId) {
      AssignmentService.subscribeToFullAssignment(
        assignmentId,
        (assignment) => {
          setFullActiveAssignment(assignment);
        },
      );
    }
  }, [assignmentId, isAssignmentAdmin, setFullActiveAssignment]);

  useEffect(() => {
    if (
      assignmentId &&
      // activeAssignment &&
      activeUser?.id &&
      isStudentApp &&
      !isAssignmentOwner &&
      !isAssignmentStudent
    ) {
      console.log("adding student to assignment");
      AssignmentService.addStudentToAssignment({
        assignmentId,
        studentUserId: activeUser?.id,
      });
    }
  }, [
    assignmentId,
    activeUser?.id,
    isStudentApp,
    userDidConsentToAssignment,
    isAssignmentOwner,
    // activeAssignment,
    isAssignmentStudent,
  ]);

  // useEffect(() => {
  //   if (assignmentId && activeUser?.id) {
  //     AssignmentService.subscribeToUserAssignmentResponses({
  //       assignmentId,
  //       userId: activeUser?.id,
  //       callback: (responses) => {
  //         updateCurrentUserAssignmentResponses(assignmentId, responses);
  //       },
  //     });
  //   }
  // }, [assignmentId, activeUser?.id, updateCurrentUserAssignmentResponses]);

  const handleExport = useCallback(async () => {
    Analytics.assignmentExportStudent({
      assignmentId: activeAssignment?.id,
      assignmentTitle: activeAssignment?.title,
    });

    setIsExporting(true);
    const input = exportRef.current;

    if (!input) return;

    // Store original styles
    const originalHeight = input.style.height;
    const originalOverflow = input.style.overflow;
    const originalTransform = input.style.transform;
    const originalPosition = input.style.position;
    const originalZIndex = input.style.zIndex;
    const originalPaddingTop = input.style.paddingTop;

    // Temporarily modify the element for export
    input.style.height = "auto";
    input.style.overflow = "visible";
    input.style.position = "fixed";
    input.style.top = "0";
    input.style.left = "0";
    // input.style.zIndex = "-9999";
    input.style.paddingTop = "10px";

    // Force a specific zoom level by scaling the content
    const targetZoom = 1.0; // Always export at 100% zoom
    const currentZoom = window.devicePixelRatio || 1;
    const scaleFactor = targetZoom / currentZoom;

    input.style.transform = `scale(${scaleFactor})`;
    input.style.transformOrigin = "top left";

    await new Promise((resolve) => setTimeout(resolve, 100));

    const fullCanvas = await html2canvas(input, {
      scale: 2, // Use fixed scale since we're controlling zoom via transform
      useCORS: true,
      scrollY: -window.scrollY,
      ignoreElements: (element) => element.classList.contains("no-export"),
    });

    const pdf = new jsPDF("p", "pt", "a4");
    const pageHeight = pdf.internal.pageSize.getHeight();
    const pageWidth = pdf.internal.pageSize.getWidth();

    const imgWidth = fullCanvas.width;
    const imgHeight = fullCanvas.height;

    const pxPerPt = imgWidth / pageWidth;
    const margin = 40;
    const availableWidth = pageWidth - margin;

    let yOffset = 0;
    let pageIndex = 0;

    while (yOffset < imgHeight) {
      const pageCanvas = document.createElement("canvas");
      const context = pageCanvas.getContext("2d");

      const sliceHeight = Math.min(imgHeight - yOffset, pageHeight * pxPerPt);
      pageCanvas.width = imgWidth;
      pageCanvas.height = sliceHeight;

      context?.drawImage(
        fullCanvas,
        0,
        yOffset,
        imgWidth,
        sliceHeight,
        0,
        0,
        imgWidth,
        sliceHeight,
      );

      const imgData = pageCanvas.toDataURL("image/png");
      const imgAspectRatio = pageCanvas.width / pageCanvas.height;
      const pdfImgHeight = availableWidth / imgAspectRatio;
      const xOffset = margin / 2;

      if (pageIndex > 0) pdf.addPage();
      pdf.addImage(imgData, "PNG", xOffset, 20, availableWidth, pdfImgHeight);

      yOffset += sliceHeight;
      pageIndex++;
    }

    pdf.save(
      `${activeAssignment?.title}-${activeUser?.email}-${new Date().toLocaleString(
        undefined,
        {
          timeZoneName: "short",
        },
      )}.pdf`,
    );

    // Restore all original styles
    input.style.height = originalHeight;
    input.style.overflow = originalOverflow || "auto";
    input.style.transform = originalTransform;
    input.style.position = originalPosition;
    input.style.zIndex = originalZIndex;
    input.style.paddingTop = originalPaddingTop;

    setIsExporting(false);
  }, [activeAssignment?.id, activeAssignment?.title, activeUser?.email]);
  const MainInfo = useMemo(() => {
    return (
      <Stack px={0} maw={MAX_WIDTH} gap={0} w="100%">
        <Group
          w="100%"
          justify="space-between"
          p="xl"
          py="lg"
          bg={
            assignmentColor
              ? lighten(theme.colors[assignmentColor][0], 0.4)
              : theme.colors.background[0]
          }
          gap="xs"
          style={{
            borderRadius: 8,
            border: `2px solid ${
              assignmentColor
                ? theme.colors[assignmentColor][0]
                : theme.colors.border[3]
            }`,
          }}
        >
          <Group>
            <Text titleStyle="h4" fw={500} lh={1.2}>
              Status:{" "}
            </Text>
            <UserAssignmentStatus
              userPlaylistData={userPlaylistData}
              userVideoData={userVideoData}
              userVideosWatched={userVideosWatched}
              isLarge
              assignmentPrompts={activeAssignment?.prompts}
              userResponses={currentUserAssignmentResponses}
            />
          </Group>
          <Group>
            <Button
              color={assignmentColor}
              size="xs"
              variant="light"
              onClick={handleExport}
              leftSection={<IconDownload size={16} />}
              loading={isExporting}
              disabled={isExporting}
              className="no-export"
            >
              Export Results
            </Button>
          </Group>
        </Group>

        {isExportView ? null : (
          <>
            <Space h="md" />
            <Stack
              p="xl"
              py="xl"
              gap={0}
              bg={theme.colors.background[0]}
              style={{
                borderRadius: 8,
                border: `2px solid ${theme.colors.border[3]}`,
              }}
            >
              <Group align="flex-start" wrap="nowrap" flex={1} gap="xs">
                <Title order={1} size="h2" lh={1.1} pt={4}>
                  {activeAssignment?.title}
                </Title>
              </Group>

              <Space h="md" />
              <Text c="text.7" fw={700} style={{ textDecoration: "underline" }}>
                Instructions
              </Text>
              <Space h="xs" />

              <Text c="text.7" fw={500}>
                {activeAssignment?.instructions}
              </Text>

              {activePartnerAdvizerCount > 0 ? (
                <>
                  <Space h="xs" />
                  <Divider w="100%" />
                  <Space h="md" />
                  <Text c="text.7" fw={500}>
                    ⭐ You can watch Advizer videos from{" "}
                    <strong>
                      {activePartnerAdvizerCount}{" "}
                      {activeUserPartner?.alumniWording?.plural || `${activeUserPartner?.title || ""} alumni`}
                    </strong>
                    .{" "}
                    <Link
                      to={`/explore?alumFilterPartnerId=${activeUserPartner?.id}`}
                      target="_blank"
                      onClick={() => {
                        setActiveFilterPartnerId(activeUserPartner?.id);
                      }}
                    >
                      Click here to explore them!
                    </Link>
                  </Text>
                </>
              ) : null}

              <>
                <Space h="md" />{" "}
                <Group w="100%" justify="flex-start" px={0}>
                  <SupportVideoButton
                    supportVideoId="assignmentStudentExperience"
                    title="Learn more"
                    shouldShowTitle
                    buttonProps={{
                      variant: "light",
                      opacity: 1,
                    }}
                  />
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={<IconFilter size={16} />}
                    component={Link}
                    to="/explore"
                    target="_blank"
                    color="primary"
                  >
                    Advizers
                  </Button>
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={<IconSearch size={16} />}
                    component={Link}
                    to="/playlists"
                    target="_blank"
                    color="primary"
                  >
                    Playlists
                  </Button>
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={<IconBrain size={16} />}
                    component={Link}
                    to="/?step=select-subjects"
                    target="_blank"
                    color="primary"
                  >
                    Get Recs
                  </Button>
                </Group>
              </>

              {/* {isAnyAdvizersAssignment &&
              activeAssignment?.totalMinutesToComplete ? (
                <>
                  <Space h="xs" />
                  <Divider w="100%" />
                  <Space h="xs" />
                  <Text
                    size="md"
                    c="text.7"
                    flex={1}
                    fw={500}
                    style={{ fontStyle: "italic" }}
                  >
                    Watch a minimum of{" "}
                    <span style={{ fontWeight: 700 }}>
                      {activeAssignment?.totalMinutesToComplete} minutes
                    </span>{" "}
                    of Advizer videos{" "}
                    {activeAssignment?.requiredCount > 1
                      ? `(from at least ${activeAssignment?.requiredCount} Advizers) `
                      : ""}
                    to complete this assignment.
                  </Text>
                </>
              ) : null} */}
            </Stack>
            {activeAssignment?.sessionSchedulingLink ? (
              <>
                <Space h="md" />
                <AssignmentSessionScheduler />
              </>
            ) : null}
          </>
        )}
      </Stack>
    );
  }, [
    activeAssignment?.instructions,
    activeAssignment?.prompts,
    activeAssignment?.sessionSchedulingLink,
    activeAssignment?.title,
    activePartnerAdvizerCount,
    activeUserPartner?.id,
    activeUserPartner?.title,
    assignmentColor,
    currentUserAssignmentResponses,
    handleExport,
    isExportView,
    isExporting,
    setActiveFilterPartnerId,
    theme.colors,
    userPlaylistData,
    userVideoData,
    userVideosWatched,
  ]);

  const AssignmentProgress = useMemo(() => {
    return (
      <Stack
        w="100%"
        px="xl"
        py="xl"
        gap={0}
        maw={MAX_WIDTH}
        bg={theme.colors.background[0]}
        style={{
          borderRadius: 8,
          border: `2px solid ${theme.colors.border[3]}`,
        }}
      >
        <Group align="center">
          {isExportView ? (
            <Stack gap={0}>
              <Text titleStyle="h3">Advize Assignment Results</Text>
              <Space h="xs" />
              <Text titleStyle="h5">
                Student:{" "}
                <span style={{ fontWeight: 500, marginLeft: 4 }}>
                  {activeUser?.email}
                </span>
              </Text>
              <Text titleStyle="h5">
                Assignment:{" "}
                <span style={{ fontWeight: 500, marginLeft: 4 }}>
                  {activeAssignment?.title}
                </span>
              </Text>
              <Text titleStyle="h5">
                Exported at:{" "}
                <span style={{ fontWeight: 500, marginLeft: 4 }}>
                  {new Date().toLocaleString(undefined, {
                    timeZoneName: "short",
                  })}
                </span>
              </Text>
            </Stack>
          ) : (
            <Stack gap={4}>
              <Text flex={1} pt={2} titleStyle="h3" w="100%" c="text.9">
                My Checklist
              </Text>

              <Text c="text.7" fw={400} size="sm">
                Your checklist and assignment status will update automatically
                as you watch videos, provide responses, etc.
              </Text>
            </Stack>
          )}
        </Group>
        <Space h={isAnyAdvizersAssignment ? "sm" : "xl"} />
        <AssignmentChecklistItems
          isExportView={isExportView}
          userVideoData={userVideoData}
          userVideosWatched={userVideosWatched}
          userPlaylistData={userPlaylistData}
          userResponses={currentUserAssignmentResponses}
        />
      </Stack>
    );
  }, [
    activeAssignment?.title,
    activeUser?.email,
    currentUserAssignmentResponses,
    isAnyAdvizersAssignment,
    isExportView,
    theme.colors.background,
    theme.colors.border,
    userPlaylistData,
    userVideoData,
    userVideosWatched,
  ]);

  const MyAdvizers = useMemo(() => {
    if (!isAnyAdvizersAssignment) return null;
    if (!activeAssignment?.totalMinutesToComplete) return null;

    return (
      <Stack
        w="100%"
        px="xl"
        py="xl"
        gap={0}
        maw={MAX_WIDTH}
        bg={theme.colors.background[0]}
        style={{
          borderRadius: 8,
          border: `2px solid ${theme.colors.border[3]}`,
        }}
      >
        <Group align="center">
          <Stack gap={4}>
            <Text
              id="my-advizers"
              flex={1}
              pt={2}
              titleStyle="h3"
              w="100%"
              c="text.9"
            >
              My Advizers
            </Text>
            {isSpecificPlaylistsAssignment || isCareerLeaderUploadAssignment ? (
              "Your checklist will update automatically as you watch videos."
            ) : (
              <Text c="text.7" fw={400} size="sm">
                Any {isAnyAdvizersAssignment ? "Advizers" : "playlists"} you
                watch will show up here.{" "}
                <Link to="/profile" target="_blank">
                  Go to your profile
                </Link>{" "}
                to see more details about your watch history.
              </Text>
            )}
          </Stack>
        </Group>
        <Space h={isAnyAdvizersAssignment ? "sm" : "xl"} />
        <AssignmentChecklistItems
          advizersOnly
          isExportView={isExportView}
          userVideoData={userVideoData}
          userVideosWatched={userVideosWatched}
          userPlaylistData={userPlaylistData}
          userResponses={currentUserAssignmentResponses}
        />
      </Stack>
    );
  }, [
    activeAssignment?.totalMinutesToComplete,
    currentUserAssignmentResponses,
    isAnyAdvizersAssignment,
    isCareerLeaderUploadAssignment,
    isExportView,
    isSpecificPlaylistsAssignment,
    theme.colors.background,
    theme.colors.border,
    userPlaylistData,
    userVideoData,
    userVideosWatched,
  ]);

  const Playlists = useMemo(() => {
    if (!activeAssignment) return null;

    if (activeAssignment?.assignmentType !== "specificPlaylists") return null;

    return (
      <>
        <Divider w="100%" maw={MAX_WIDTH} my="xl" />
        <Stack className="no-export" px={0} maw={MAX_WIDTH} gap={0}>
          <Stack maw={MAX_WIDTH} gap="xl">
            {assignmentPlaylists.map((playlist) => (
              <PlaylistCard key={playlist.id} playlistId={playlist.id} />
            ))}
          </Stack>
        </Stack>
      </>
    );
  }, [activeAssignment, assignmentPlaylists]);

  const ExportingOverlay = useMemo(() => {
    if (!isExportView) return null;

    return (
      <Stack
        pos="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="background.1"
        align="center"
        justify="center"
        opacity={0.8}
        style={{
          zIndex: 9999,
        }}
        // gap="xl"
      >
        <Loader size="lg" />
        <Text titleStyle="h3">Exporting...</Text>
      </Stack>
    );
  }, [isExportView]);

  const isAssignmentLoading = useMemo(() => {
    return !activeAssignment?.id;
  }, [activeAssignment?.id]);

  if (isAssignmentLoading) {
    return (
      <Stack h="100vh" align="center" justify="center">
        <Loader size="xl" />
      </Stack>
    );
  }

  if (activeAssignment?.removed) {
    return (
      <Stack pt={100} w={600} align="center" mx="auto">
        <Text>This assignment does not exist or has been removed.</Text>
      </Stack>
    );
  }

  return (
    <>
      <Helmet>
        <title>{activeAssignment?.title}</title>
      </Helmet>

      <AssignmentConsentConfirmation />

      <Stack
        ref={exportRef}
        pt={styles.dimensions.headerHeight + 80}
        pb={100}
        px="md"
        style={{ overflowY: "auto" }}
        bg="background.1"
        h="100vh"
        align="center"
      >
        {isAssignmentOwner && !isStudentView ? (
          <AssignmentResults />
        ) : (
          <>
            {MainInfo}
            {AssignmentProgress}
            {Playlists}
            {MyAdvizers}
          </>
        )}
      </Stack>
      {ExportingOverlay}
    </>
  );
}
